/**
 * Compose — the pure half (`docs/adr/6017`). What the planner typed, checked here on strings and
 * tested; the platform still judges the question (its 422s are drawn in words beside the form).
 *
 * Nothing is defaulted that the planner did not choose: no dates, no channel, no doors, no mechanic.
 * The form starts with ONE empty alternative — one is enough since the owner ruling of 2026-10-09
 * (bata-platform ADR 6076) — and never offers a "no promotion" one: every alternative is a governed
 * mechanic. With two or more, the comparator is the first alternative, the contract's own default,
 * said on the form and changeable.
 *
 * Doors may be named by commercial district, and articles by their own codes: a typed list, checked
 * against the article master before «Simula» is offered (ADR 6076). A listed article in the Zona sale
 * or the outlet blocks «Simula»: the Zona sale and the campaigns are exclusive (bata-reqs issue 35
 * C4, bata-platform ADR 6078), and the platform refuses it too.
 */

import {
  ARTICLE_SCOPE_CLASS, CAMPAIGN_CHANNELS, campaignSimulationOf, MAX_ALTERNATIVES, MAX_ARTICLES_PER_LOOKUP, MAX_LABEL_LENGTH, MIN_ALTERNATIVES,
  SCOPE_CLASSES, type CampaignChannel, type CampaignMechanic, type CampaignSimulationShape, type ComposedAlternative, type ComposedCampaign,
  type ComposedScopeRow, type ParameterKind, type ScopeClass, type StoresKind,
} from '@/api/pending/campaigns';

export interface DraftAlternative {
  readonly label: string;
  readonly mechanic: CampaignMechanic | null;
  /** As typed: a percentage, `''` when none. */
  readonly depth: string;
  readonly buy: string;
  readonly pay: string;
}

export interface DraftRow {
  readonly kind: ScopeClass;
  readonly value: string;
  /** A `SUB_CATEGORY` row's category, from the options read (bata-platform ADR 6071). */
  readonly category?: string | null;
}

/** A draft row as the question carries it: trimmed, its category kept on a sub-category only. Pure. */
function composedRow(row: DraftRow): ComposedScopeRow {
  const value = row.value.trim();
  return row.kind === 'SUB_CATEGORY' && row.category !== undefined && row.category !== null
    ? { kind: row.kind, value, category: row.category }
    : { kind: row.kind, value };
}

export interface CampaignDraft {
  readonly startsOn: string;
  readonly endsOn: string;
  readonly channel: CampaignChannel | null;
  readonly storesKind: StoresKind | null;
  readonly clusters: readonly string[];
  /** The commercial districts chosen (ADR 6076). */
  readonly districts: readonly string[];
  /** Door keys as typed, separated by commas, spaces or new lines. */
  readonly doors: string;
  /** Article codes as typed, the same separators (ADR 6076): included by code, beside the rows. */
  readonly articles: string;
  readonly include: readonly DraftRow[];
  readonly exclude: readonly DraftRow[];
  readonly alternatives: readonly DraftAlternative[];
  readonly comparator: number;
}

export const EMPTY_ALTERNATIVE: DraftAlternative = { label: '', mechanic: null, depth: '', buy: '', pay: '' };

/** The form's first state: nothing chosen, one empty alternative (ADR 6076), the comparator once there are two. */
export function emptyDraft(): CampaignDraft {
  return {
    startsOn: '', endsOn: '', channel: null, storesKind: null, clusters: [], districts: [], doors: '', articles: '', include: [],
    exclude: [], alternatives: [EMPTY_ALTERNATIVE], comparator: 0,
  };
}

/** A real calendar day written `YYYY-MM-DD`. Pure. */
export function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

/** The window's length in days, both ends included. Pure. */
export function windowDays(startsOn: string, endsOn: string): number {
  return Math.round((Date.parse(`${endsOn}T00:00:00Z`) - Date.parse(`${startsOn}T00:00:00Z`)) / 86_400_000) + 1;
}

/** Door keys as typed: split on commas, spaces and new lines, blanks dropped, first spelling kept. Pure. */
export function doorKeys(text: string): readonly string[] {
  return [...new Set(text.split(/[\s,;]+/).map((key) => key.trim()).filter((key) => key !== ''))];
}

/** Article codes as typed: the door list's rule — split, blanks dropped, repeats once. Pure. */
export const articleKeys = doorKeys;

/** The article list as include rows of kind `ARTICLE`, in the order typed. Pure. */
export function articleRows(text: string): readonly ComposedScopeRow[] {
  return articleKeys(text).map((value) => ({ kind: ARTICLE_SCOPE_CLASS, value }));
}

const DECIMAL = /^\d+(\.\d+)?$/;
const WHOLE = /^\d+$/;

/** Why one alternative cannot be sent, as a catalogue key — `null` when it can. */
export type AlternativeProblem =
  | 'label' | 'labelLong' | 'mechanic' | 'notGoverned' | 'depth' | 'quantities' | 'duplicate';

export const ALTERNATIVE_PROBLEM_KEY: Readonly<Record<AlternativeProblem, string>> = {
  label: 'inseason.campaigns.compose.problem.label',
  labelLong: 'inseason.campaigns.compose.problem.labelLong',
  mechanic: 'inseason.campaigns.compose.problem.mechanic',
  notGoverned: 'inseason.campaigns.compose.problem.notGoverned',
  depth: 'inseason.campaigns.compose.problem.depth',
  quantities: 'inseason.campaigns.compose.problem.quantities',
  duplicate: 'inseason.campaigns.compose.problem.duplicate',
};

/** The ONE reason «Simula» is blocked, said under it — the first that applies, in the form's order. */
export type ComposeBlock =
  | 'run' | 'scenario' | 'dates' | 'endsBeforeStart' | 'channel' | 'stores' | 'clusters' | 'districts' | 'doors' | 'scopeRow'
  | 'articlesTooMany' | 'articlesChecking' | 'articlesUnknown' | 'articlesInMarkdown' | 'tooFew' | 'tooMany' | 'alternatives';

export const COMPOSE_BLOCK_KEY: Readonly<Record<ComposeBlock, string>> = {
  run: 'inseason.campaigns.compose.block.run',
  scenario: 'inseason.campaigns.compose.block.scenario',
  dates: 'inseason.campaigns.compose.block.dates',
  endsBeforeStart: 'inseason.campaigns.compose.block.endsBeforeStart',
  channel: 'inseason.campaigns.compose.block.channel',
  stores: 'inseason.campaigns.compose.block.stores',
  clusters: 'inseason.campaigns.compose.block.clusters',
  districts: 'inseason.campaigns.compose.block.districts',
  doors: 'inseason.campaigns.compose.block.doors',
  scopeRow: 'inseason.campaigns.compose.block.scopeRow',
  articlesTooMany: 'inseason.campaigns.compose.block.articlesTooMany',
  articlesChecking: 'inseason.campaigns.compose.block.articlesChecking',
  articlesUnknown: 'inseason.campaigns.compose.block.articlesUnknown',
  articlesInMarkdown: 'inseason.campaigns.compose.block.articlesInMarkdown',
  tooFew: 'inseason.campaigns.compose.block.tooFew',
  tooMany: 'inseason.campaigns.compose.block.tooMany',
  alternatives: 'inseason.campaigns.compose.block.alternatives',
};

export interface ComposeCheck {
  /** The question, complete — `null` while anything blocks it. */
  readonly question: ComposedCampaign | null;
  readonly block: ComposeBlock | null;
  /** One entry per alternative, in order: what stops it, or `null`. */
  readonly alternatives: readonly (AlternativeProblem | null)[];
}

interface ComposeContext {
  readonly runId: string | null;
  readonly scenarioId: string | null;
  /** The mechanics the vocabulary in force governs. */
  readonly governed: readonly CampaignMechanic[];
  /** The parameter each mechanic takes (the fit's word, else the ceiling's). */
  readonly parameterOf: (mechanic: CampaignMechanic) => ParameterKind;
  /**
   * The typed article list against the article master: `pending` while the list read is not yet
   * the one typed, `unknown` the codes the master does not carry, `markdown` the codes in the Zona
   * sale or the outlet for the window — never in a campaign (bata-platform ADR 6078). Absent: no
   * list is typed.
   */
  readonly articleCheck?: { readonly pending: boolean; readonly unknown: readonly string[]; readonly markdown?: readonly string[] };
}

function alternativeOf(row: DraftAlternative, context: ComposeContext): ComposedAlternative | AlternativeProblem {
  const label = row.label.trim();
  if (label === '') return 'label';
  if (label.length > MAX_LABEL_LENGTH) return 'labelLong';
  if (row.mechanic === null) return 'mechanic';
  if (!context.governed.includes(row.mechanic)) return 'notGoverned';
  if (context.parameterOf(row.mechanic) === 'percent_off') {
    const depth = row.depth.trim();
    const value = DECIMAL.test(depth) ? Number(depth) : Number.NaN;
    if (!(value > 0 && value < 100)) return 'depth';
    return { label, mechanic: row.mechanic, depthPct: value, buy: null, pay: null };
  }
  const buy = WHOLE.test(row.buy.trim()) ? Number(row.buy.trim()) : Number.NaN;
  const pay = WHOLE.test(row.pay.trim()) ? Number(row.pay.trim()) : Number.NaN;
  // Buy N, pay M: at least one item is free, and at least one is paid for.
  if (!(pay >= 1 && buy > pay)) return 'quantities';
  return { label, mechanic: row.mechanic, depthPct: null, buy, pay };
}

const identity = (row: ComposedAlternative): string => JSON.stringify([row.mechanic, row.depthPct, row.buy, row.pay]);

/** The draft checked: the question when it is complete, else the first reason it is not. Pure. */
export function checkDraft(draft: CampaignDraft, context: ComposeContext): ComposeCheck {
  const read = draft.alternatives.map((row) => alternativeOf(row, context));
  const seen = new Set<string>();
  const problems = read.map((row): AlternativeProblem | null => {
    if (typeof row === 'string') return row;
    const key = identity(row);
    if (seen.has(key)) return 'duplicate';
    seen.add(key);
    return null;
  });
  const doors = doorKeys(draft.doors);
  const articles = articleKeys(draft.articles);
  const rowsComplete = [...draft.include, ...draft.exclude].every((row) => row.value.trim() !== '');

  let block: ComposeBlock | null = null;
  if (context.runId === null) block = 'run';
  else if (context.scenarioId === null) block = 'scenario';
  else if (!isCalendarDate(draft.startsOn) || !isCalendarDate(draft.endsOn)) block = 'dates';
  else if (draft.endsOn < draft.startsOn) block = 'endsBeforeStart';
  else if (draft.channel === null) block = 'channel';
  else if (draft.storesKind === null) block = 'stores';
  else if (draft.storesKind === 'clusters' && draft.clusters.length === 0) block = 'clusters';
  else if (draft.storesKind === 'districts' && draft.districts.length === 0) block = 'districts';
  else if (draft.storesKind === 'doors' && doors.length === 0) block = 'doors';
  else if (!rowsComplete) block = 'scopeRow';
  else if (articles.length > MAX_ARTICLES_PER_LOOKUP) block = 'articlesTooMany';
  else if (articles.length > 0 && (context.articleCheck === undefined || context.articleCheck.pending)) block = 'articlesChecking';
  else if (articles.length > 0 && (context.articleCheck?.unknown.length ?? 0) > 0) block = 'articlesUnknown';
  else if (articles.length > 0 && (context.articleCheck?.markdown?.length ?? 0) > 0) block = 'articlesInMarkdown';
  else if (draft.alternatives.length < MIN_ALTERNATIVES) block = 'tooFew';
  else if (draft.alternatives.length > MAX_ALTERNATIVES) block = 'tooMany';
  else if (problems.some((problem) => problem !== null)) block = 'alternatives';

  if (block !== null || context.runId === null || context.scenarioId === null || draft.channel === null || draft.storesKind === null) {
    return { question: null, block, alternatives: problems };
  }
  const stores = draft.storesKind === 'clusters' ? { kind: 'clusters' as const, clusters: draft.clusters }
    : draft.storesKind === 'districts' ? { kind: 'districts' as const, districts: draft.districts }
      : draft.storesKind === 'doors' ? { kind: 'doors' as const, doors } : { kind: 'all' as const };
  return {
    question: {
      baseRunId: context.runId,
      scenarioId: context.scenarioId,
      startsOn: draft.startsOn,
      endsOn: draft.endsOn,
      channel: draft.channel,
      stores,
      include: [...draft.include.map(composedRow), ...articleRows(draft.articles)],
      exclude: draft.exclude.map(composedRow),
      alternatives: read.filter((row): row is ComposedAlternative => typeof row !== 'string'),
      comparator: Math.min(Math.max(draft.comparator, 0), draft.alternatives.length - 1),
    },
    block: null,
    alternatives: problems,
  };
}

/** Remove one alternative, keeping the comparator on the same alternative (or the first). Pure. */
export function removeAlternative(draft: CampaignDraft, index: number): CampaignDraft {
  const alternatives = draft.alternatives.filter((_, at) => at !== index);
  const comparator = draft.comparator === index ? 0 : draft.comparator > index ? draft.comparator - 1 : draft.comparator;
  return { ...draft, alternatives, comparator };
}

/** Whether the scope-count read can be asked: a start date, a channel and complete doors. Pure. */
export function countable(draft: CampaignDraft): boolean {
  if (!isCalendarDate(draft.startsOn) || draft.channel === null || draft.storesKind === null) return false;
  if (draft.storesKind === 'clusters' && draft.clusters.length === 0) return false;
  if (draft.storesKind === 'districts' && draft.districts.length === 0) return false;
  if (draft.storesKind === 'doors' && doorKeys(draft.doors).length === 0) return false;
  return [...draft.include, ...draft.exclude].every((row) => row.value.trim() !== '');
}

/**
 * The draft a stored campaign simulation asked (owner ruling 2026-10-20, «Modifica e rilancia»):
 * the dates, the channel, the doors, the include and exclude rows (a sub-category with the category
 * it was chosen under, read from the stored row), the article codes typed as a list, the
 * alternatives and the comparator — so «Simula» sends the same body again. Pure.
 *
 * What this form cannot hold is left out and counted in `dropped`: a scope row of a class the form
 * does not offer, and a channel this build does not know. The banner is not a field: it is always
 * the answerable one, as when the question was first asked.
 */
export function draftFromCampaign(shape: CampaignSimulationShape): { readonly draft: CampaignDraft; readonly dropped: number } {
  const simulation = campaignSimulationOf(shape);
  let dropped = 0;
  const categoryOf = (side: 'include' | 'exclude', index: number): string | null => {
    const rows = shape.articleScope[side];
    const row: unknown = Array.isArray(rows) ? rows[index] : undefined;
    if (typeof row !== 'object' || row === null) return null;
    const category = (row as Readonly<Record<string, unknown>>)['category'];
    return typeof category === 'string' && category !== '' ? category : null;
  };
  const rowsOf = (side: 'include' | 'exclude'): { readonly rows: DraftRow[]; readonly articles: string[] } => {
    const rows: DraftRow[] = [];
    const articles: string[] = [];
    simulation[side].forEach((row, index) => {
      if (row.kind === ARTICLE_SCOPE_CLASS && side === 'include') articles.push(row.value);
      else if (row.kind !== null && (SCOPE_CLASSES as readonly string[]).includes(row.kind)) {
        const kind = row.kind as ScopeClass;
        const category = kind === 'SUB_CATEGORY' ? categoryOf(side, index) : null;
        rows.push(category === null ? { kind, value: row.value } : { kind, value: row.value, category });
      } else dropped += 1;
    });
    return { rows, articles };
  };
  const include = rowsOf('include');
  const exclude = rowsOf('exclude');
  const channel = (CAMPAIGN_CHANNELS as readonly string[]).includes(simulation.channel) ? simulation.channel as CampaignChannel : null;
  if (channel === null) dropped += 1;
  const stores = simulation.stores;
  const number = (value: number | null): string => (value === null ? '' : String(value));
  return {
    draft: {
      startsOn: simulation.startsOn,
      endsOn: simulation.endsOn,
      channel,
      storesKind: stores.kind,
      clusters: stores.kind === 'clusters' ? stores.clusters : [],
      districts: stores.kind === 'districts' ? stores.districts : [],
      doors: stores.kind === 'doors' ? stores.doors.join(', ') : '',
      articles: include.articles.join(', '),
      include: include.rows,
      exclude: exclude.rows,
      alternatives: simulation.alternatives.map((row) => ({
        label: row.label, mechanic: row.mechanic.code, depth: number(row.depthPct), buy: number(row.buy), pay: number(row.pay),
      })),
      comparator: simulation.comparator,
    },
    dropped,
  };
}
